import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, ComposedChart, Line, Area
} from 'recharts';
import { FaCalendarAlt, FaChartBar, FaChartLine, FaTrophy, FaCoins, FaExclamationTriangle, FaCheckCircle, FaSpinner } from 'react-icons/fa';
import styles from './RelatoriosFaturamento.module.css';

const API_URL = import.meta.env.VITE_API_URL || 'https://dashboardgringao.cloud/api';

const MESES_CURTO = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const formatCurrency = (val) => {
    if (!val && val !== 0) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
};

const formatCompact = (val) => {
    if (!val && val !== 0) return 'R$ 0';
    if (val >= 1000000) return `R$ ${(val / 1000000).toFixed(1)}M`;
    if (val >= 1000) return `R$ ${(val / 1000).toFixed(0)}k`;
    return formatCurrency(val);
};

// Tooltip personalizado
const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className={styles.tooltip}>
            <div className={styles.tooltipTitle}>{label}</div>
            {payload.map((entry, i) => (
                <div key={i} className={styles.tooltipRow}>
                    <span className={styles.tooltipDot} style={{ background: entry.color }} />
                    <span className={styles.tooltipName}>{entry.name}</span>
                    <span className={styles.tooltipValue}>
                        {entry.name?.includes('%') ? `${entry.value}%` : formatCurrency(entry.value)}
                    </span>
                </div>
            ))}
        </div>
    );
};

// Componente simples de Imagem com Tooltip nativo (Hover)
const ProductImageTooltip = ({ src, nome, codigo }) => {
    const [show, setShow] = useState(false);
    const API_URL = import.meta.env.VITE_API_URL || 'https://dashboardgringao.cloud/api';

    // Resolver a URL inteligente da Imagem (Base64 vs HTTP vs Hash do Bling)
    let imgSource = '/placeholder.svg';
    if (src && (src.startsWith('http') || src.startsWith('data:'))) {
        imgSource = src;
    } else if (src && src.trim() !== '' && codigo) {
        // Se for um Hash ou nome de arquivo que a base trouxe sujo, buscamos do proxy oficial da API
        imgSource = `${API_URL}/produtos/imagem/${codigo}`;
    }

    return (
        <div style={{ position: 'relative', display: 'flex' }}
            onMouseEnter={() => setShow(true)}
            onMouseLeave={() => setShow(false)}>
            <img
                src={imgSource}
                alt={nome}
                onError={(e) => { e.target.onerror = null; e.target.src = '/placeholder.svg'; }}
                style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 4, cursor: 'pointer', backgroundColor: '#1e293b', border: '1px solid #334155' }}
            />
            {show && (
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '100%',
                    transform: 'translateY(-50%)',
                    marginLeft: '12px',
                    zIndex: 9999,
                    backgroundColor: '#1e293b',
                    padding: '6px',
                    borderRadius: '8px',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
                    border: '1px solid #334155',
                    width: '300px',
                    pointerEvents: 'none'
                }}>
                    <img
                        src={imgSource}
                        style={{ width: '100%', height: 'auto', borderRadius: '4px' }}
                        alt={nome}
                        onError={(e) => { e.target.onerror = null; e.target.src = '/placeholder.svg'; }}
                    />
                </div>
            )}
        </div>
    );
};

function RelatoriosFaturamento() {
    // Estado dos filtros
    const [tipoPeriodo, setTipoPeriodo] = useState('anual');
    const [anoBase, setAnoBase] = useState('');
    const [anoComp, setAnoComp] = useState('');
    const [mesBase, setMesBase] = useState(String(new Date().getMonth() + 1));
    const [mesComp, setMesComp] = useState(String(new Date().getMonth() + 1));
    const [loja, setLoja] = useState('todas');
    const [abaAtiva, setAbaAtiva] = useState('resumo');

    // Dados
    const [anos, setAnos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingAnos, setLoadingAnos] = useState(false);
    const [dadosMensal, setDadosMensal] = useState(null);
    const [dadosComparacao, setDadosComparacao] = useState(null);
    const [dadosQueda, setDadosQueda] = useState(null);
    const [dadosTopVendidos, setDadosTopVendidos] = useState(null);
    const [comparou, setComparou] = useState(false);

    // Buscar anos disponíveis (ao montar e ao trocar de loja)
    useEffect(() => {
        const fetchAnos = async () => {
            setLoadingAnos(true);
            try {
                const res = await axios.get(`${API_URL}/relatorios-faturamento/anos-disponiveis`);
                if (res.data.success && res.data.anos.length > 0) {
                    setAnos(res.data.anos);
                    setAnoBase(String(res.data.anos[0]));
                    if (res.data.anos.length > 1) {
                        setAnoComp(String(res.data.anos[1]));
                    }
                }
            } catch (err) {
                console.error('Erro ao buscar anos:', err);
            } finally {
                setLoadingAnos(false);
            }
        };
        fetchAnos();
    }, [loja]);

    // Comparar - buscar TODOS os dados
    const handleComparar = async () => {
        if (!anoBase) return;
        setLoading(true);
        setComparou(true);
        try {
            let routeFaturamentoMensal = `${API_URL}/relatorios-faturamento/faturamento-mensal`;
            let routeFaturamentoComparacao = `${API_URL}/relatorios-faturamento/faturamento-mensal`; // placeholder

            let paramsBase = { ano: parseInt(anoBase), loja };
            let paramsComp = { ano: parseInt(anoComp), loja };

            if (tipoPeriodo === 'mensal') {
                routeFaturamentoMensal = `${API_URL}/relatorios-faturamento/faturamento-diario`;
                routeFaturamentoComparacao = `${API_URL}/relatorios-faturamento/faturamento-diario`;
                paramsBase.mes = parseInt(mesBase);
                paramsComp.mes = mesComp ? parseInt(mesComp) : parseInt(mesBase);
            }

            console.log(`[RelFaturamento] Buscando dados: anoBase=${anoBase}, anoComp=${anoComp}, loja=${loja}, tipo=${tipoPeriodo}`);

            const basePromise = axios.get(routeFaturamentoMensal, { params: paramsBase });

            let compPromise = Promise.resolve({ data: { success: false } });
            let hasComp = false;

            if (anoComp && anoComp !== anoBase) {
                compPromise = axios.get(routeFaturamentoComparacao, { params: paramsComp });
                hasComp = true;
            } else if (tipoPeriodo === 'mensal' && mesComp && mesComp !== mesBase && anoBase === anoComp) {
                compPromise = axios.get(routeFaturamentoComparacao, { params: { ano: parseInt(anoBase), loja, mes: parseInt(mesComp) } });
                hasComp = true;
            } else if (tipoPeriodo === 'mensal' && mesComp && mesComp !== mesBase) {
                compPromise = axios.get(routeFaturamentoComparacao, { params: { ano: parseInt(anoComp || anoBase), loja, mes: parseInt(mesComp) } });
                hasComp = true;
            }

            let quedaPromise = Promise.resolve({ data: { success: true, data: null } });
            if (tipoPeriodo === 'anual') {
                quedaPromise = axios.get(`${API_URL}/relatorios-faturamento/detectar-queda`, {
                    params: { ano: parseInt(anoBase), loja }
                });
            }

            // Buscar top vendidos do BASE
            let paramsBaseTop = { loja, limite: 20, tipoPeriodo, ano: parseInt(anoBase) };
            if (tipoPeriodo === 'mensal') {
                paramsBaseTop.anoBase = parseInt(anoBase); paramsBaseTop.anoComparacao = parseInt(anoBase);
                paramsBaseTop.mesBase = parseInt(mesBase); paramsBaseTop.mesComparacao = parseInt(mesBase);
            }

            const topVendidosPromise = axios.get(`${API_URL}/comparacao-anual/top-vendidos`, {
                params: paramsBaseTop
            });

            // Buscar top vendidos do COMP (para fazer join client-side)
            let topVendidosCompPromise = Promise.resolve({ data: { success: false } });
            let fetchCompTop = false;

            if (anoComp || mesComp) {
                const finalAnoComp = parseInt(anoComp || anoBase);
                const finalMesComp = parseInt(mesComp || mesBase);

                if (finalAnoComp !== parseInt(anoBase) || (tipoPeriodo === 'mensal' && finalMesComp !== parseInt(mesBase))) {
                    let paramsCompTop = { loja, limite: 1000, tipoPeriodo, ano: finalAnoComp };
                    if (tipoPeriodo === 'mensal') {
                        paramsCompTop.anoBase = finalAnoComp; paramsCompTop.anoComparacao = finalAnoComp;
                        paramsCompTop.mesBase = finalMesComp; paramsCompTop.mesComparacao = finalMesComp;
                    }
                    topVendidosCompPromise = axios.get(`${API_URL}/comparacao-anual/top-vendidos`, {
                        params: paramsCompTop
                    });
                    fetchCompTop = true;
                }
            }

            const [resBase, resComp, resQueda, resTop, resTopComp] = await Promise.all([
                basePromise, compPromise, quedaPromise, topVendidosPromise, topVendidosCompPromise
            ]);

            if (resBase.data.success) {
                setDadosMensal(resBase.data.data);
            }

            if (hasComp && resComp?.data?.success) {
                setDadosComparacao(resComp.data.data);
            } else {
                setDadosComparacao(null);
            }

            if (resQueda?.data?.success) {
                setDadosQueda(resQueda.data.data);
            }

            if (resTop?.data?.success) {
                let mergedTop = resTop.data.data;

                if (fetchCompTop && resTopComp?.data?.success) {
                    const compDataMap = new Map();
                    resTopComp.data.data.forEach(item => {
                        compDataMap.set(item.codigo, item);
                    });

                    mergedTop = mergedTop.map(p => {
                        const compItem = compDataMap.get(p.codigo) || {};
                        return {
                            ...p,
                            qtdBase: p.qtdVendida || 0,
                            qtdComp: compItem.qtdVendida || 0,
                            valorBase: p.valorTotal || 0,
                            valorComp: compItem.valorTotal || 0
                        };
                    });
                } else {
                    mergedTop = mergedTop.map(p => ({
                        ...p,
                        qtdBase: p.qtdVendida || 0,
                        qtdComp: 0,
                        valorBase: p.valorTotal || 0,
                        valorComp: 0
                    }));
                }

                setDadosTopVendidos(mergedTop);
            }
        } catch (err) {
            console.error('Erro ao buscar dados:', err);
        } finally {
            setLoading(false);
        }
    };

    // Auto-recarregar ao mudar a loja (após a primeira comparação)
    const prevLojaRef = useRef(loja);
    useEffect(() => {
        if (prevLojaRef.current !== loja) {
            prevLojaRef.current = loja;
            if (comparou && anoBase) {
                // Pequeno delay para garantir que o state de loja já atualizou
                const timer = setTimeout(() => handleComparar(), 50);
                return () => clearTimeout(timer);
            }
        }
    }, [loja]);



    // ============================
    // ABA: RESUMO GERAL
    // ============================
    const renderResumoGeral = () => {
        if (!dadosMensal) return renderEmpty();

        const temComparacao = dadosComparacao !== null;
        const maxValor = Math.max(...dadosMensal.meses.map(m => m.total.valor));

        const labelBase = tipoPeriodo === 'mensal' ? `${mesBase}/${anoBase}` : anoBase;
        // Padrão de labelComp depende de ter mesComp ou anoComp selecionado
        const labelComp = tipoPeriodo === 'mensal' ? `${mesComp}/${anoComp || anoBase}` : anoComp;

        const chartData = dadosMensal.meses.map((m, i) => {
            const item = {
                nome: tipoPeriodo === 'mensal' ? m.nome : MESES_CURTO[m.mes - 1],
                [labelBase]: m.total.valor,
            };
            if (temComparacao) {
                item[labelComp] = dadosComparacao.meses[i]?.total.valor || 0;
            }
            return item;
        });

        const totalAnoAtual = dadosMensal.totalAno.valor;
        const totalAnoComp = temComparacao
            ? dadosComparacao.meses.reduce((s, m) => s + m.total.valor, 0)
            : 0;
        const variacao = temComparacao && totalAnoComp > 0
            ? ((totalAnoAtual - totalAnoComp) / totalAnoComp * 100).toFixed(1)
            : null;

        return (
            <>
                {/* KPI Cards */}
                <div className={styles.kpiRow}>
                    <div className={styles.kpiCard}>
                        <span className={styles.kpiLabel}>Total {anoBase}</span>
                        <span className={styles.kpiValue}>{formatCurrency(totalAnoAtual)}</span>
                    </div>
                    <div className={styles.kpiCard}>
                        <span className={styles.kpiLabel}>Média/Mês</span>
                        <span className={styles.kpiValue}>{formatCurrency(dadosMensal.totalAno.mediaMensal)}</span>
                    </div>
                    {temComparacao && (
                        <>
                            <div className={styles.kpiCard}>
                                <span className={styles.kpiLabel}>Total {anoComp}</span>
                                <span className={styles.kpiValue}>{formatCurrency(totalAnoComp)}</span>
                            </div>
                            <div className={styles.kpiCard}>
                                <span className={styles.kpiLabel}>Variação</span>
                                <span className={`${styles.kpiValue} ${parseFloat(variacao) >= 0 ? styles.textUp : styles.textDown}`}>
                                    {parseFloat(variacao) >= 0 ? '▲' : '▼'} {Math.abs(parseFloat(variacao))}%
                                </span>
                            </div>
                        </>
                    )}
                </div>

                {/* KPIs E-commerce Extras */}
                {dadosMensal.kpisEcommerce && (() => {
                    const kBase = dadosMensal.kpisEcommerce;
                    const kComp = temComparacao && dadosComparacao?.kpisEcommerce ? dadosComparacao.kpisEcommerce : null;

                    const calcVar = (atual, anterior) => {
                        if (!anterior || anterior === 0) return null;
                        return ((atual - anterior) / anterior * 100).toFixed(1);
                    };

                    const renderKpiComComp = (label, valorBase, valorComp, cor, isCurrency = true) => {
                        const v = calcVar(valorBase, valorComp);
                        return (
                            <div className={styles.kpiCard} style={{ borderLeft: `3px solid ${cor}` }}>
                                <span className={styles.kpiLabel}>{label}</span>
                                <span className={styles.kpiValue} style={{ fontSize: '1.15rem', color: cor }}>
                                    {isCurrency ? formatCurrency(valorBase) : valorBase?.toLocaleString('pt-BR')}
                                </span>
                                {kComp && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                                        <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                            {labelComp}: {isCurrency ? formatCurrency(valorComp) : valorComp?.toLocaleString('pt-BR')}
                                        </span>
                                        {v !== null && (
                                            <span style={{
                                                fontSize: '0.7rem',
                                                padding: '1px 6px',
                                                borderRadius: '4px',
                                                backgroundColor: parseFloat(v) >= 0 ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                                                color: parseFloat(v) >= 0 ? '#22c55e' : '#ef4444',
                                                fontWeight: 600
                                            }}>
                                                {parseFloat(v) >= 0 ? '+' : ''}{v}%
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    };

                    return (
                        <div className={styles.kpiRow} style={{ marginTop: '8px' }}>
                            {renderKpiComComp('Pedidos Atendidos', kBase.pedidos, kComp?.pedidos, '#3b82f6', false)}
                            {renderKpiComComp('Frete Total', kBase.frete, kComp?.frete, '#8b5cf6', true)}
                            {renderKpiComComp('Ticket Médio', kBase.ticketMedio, kComp?.ticketMedio, '#f59e0b', true)}
                            {renderKpiComComp(
                                `Cancelados (${kBase.cancelados.pedidos})`,
                                kBase.cancelados.valor,
                                kComp?.cancelados?.valor,
                                '#ef4444',
                                true
                            )}
                        </div>
                    );
                })()}

                {/* Gráfico */}
                <div className={styles.chartCard}>
                    <div className={styles.chartHeader}>
                        Faturamento {tipoPeriodo === 'mensal' ? 'Diário' : 'Mensal'} {temComparacao ? `— ${labelBase} vs ${labelComp}` : `— ${labelBase}`}
                    </div>
                    <div className={styles.chartBody}>
                        <ResponsiveContainer width="100%" height={320}>
                            <BarChart data={chartData} barGap={temComparacao ? 2 : 0} barCategoryGap="20%">
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                                <XAxis dataKey="nome" stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} />
                                <YAxis stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} tickFormatter={formatCompact} />
                                <Tooltip content={<ChartTooltip />} />
                                <Legend wrapperStyle={{ fontSize: '0.8rem', color: '#6b7280' }} />
                                <Bar dataKey={String(anoBase)} fill="#3b82f6" radius={[4, 4, 0, 0]} name={String(anoBase)} />
                                {temComparacao && (
                                    <Bar dataKey={String(anoComp)} fill="#cbd5e1" radius={[4, 4, 0, 0]} name={String(anoComp)} opacity={0.7} />
                                )}
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Tabela */}
                <div className={styles.chartCard}>
                    <div className={styles.chartHeader}>Detalhamento por {tipoPeriodo === 'mensal' ? 'Dia' : 'Mês'}</div>
                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>{tipoPeriodo === 'mensal' ? 'Dia' : 'Mês'}</th>
                                    {loja === 'todas' && <th className={styles.right}>Centro</th>}
                                    {loja === 'todas' && <th className={styles.right}>Caxias</th>}
                                    {loja === 'todas' && <th className={styles.right}>E-commerce</th>}
                                    <th className={styles.right}>Total {labelBase}</th>
                                    {temComparacao && <th className={styles.right}>Total {labelComp}</th>}
                                    {temComparacao && <th className={styles.right}>Var %</th>}
                                    <th style={{ width: '20%' }}>Proporção</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dadosMensal.meses.map((m, i) => {
                                    const pct = maxValor > 0 ? (m.total.valor / maxValor * 100) : 0;
                                    const valorComp = temComparacao ? (dadosComparacao.meses[i]?.total.valor || 0) : 0;
                                    const varMes = temComparacao && valorComp > 0
                                        ? ((m.total.valor - valorComp) / valorComp * 100).toFixed(1)
                                        : null;

                                    return (
                                        <tr key={m.mes}>
                                            <td className={styles.monthName}>{m.nome}</td>
                                            {loja === 'todas' && <td className={styles.right}>{formatCurrency(m.centro.valor)}</td>}
                                            {loja === 'todas' && <td className={styles.right}>{formatCurrency(m.caxias.valor)}</td>}
                                            {loja === 'todas' && <td className={styles.right}>{formatCurrency(m.ecommerce.valor)}</td>}
                                            <td className={`${styles.right} ${styles.bold}`}>{formatCurrency(m.total.valor)}</td>
                                            {temComparacao && <td className={`${styles.right} ${styles.muted}`}>{formatCurrency(valorComp)}</td>}
                                            {temComparacao && (
                                                <td className={styles.right}>
                                                    {varMes !== null ? (
                                                        <span className={`${styles.varBadge} ${parseFloat(varMes) >= 0 ? styles.varUp : styles.varDown}`}>
                                                            {parseFloat(varMes) >= 0 ? '+' : ''}{varMes}%
                                                        </span>
                                                    ) : '—'}
                                                </td>
                                            )}
                                            <td>
                                                <div className={styles.propBar}>
                                                    <div className={styles.propFill} style={{ width: `${pct}%` }} />
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td className={styles.bold}>TOTAL</td>
                                    {loja === 'todas' && <td className={`${styles.right} ${styles.bold}`}>{formatCurrency(dadosMensal.meses.reduce((s, m) => s + m.centro.valor, 0))}</td>}
                                    {loja === 'todas' && <td className={`${styles.right} ${styles.bold}`}>{formatCurrency(dadosMensal.meses.reduce((s, m) => s + m.caxias.valor, 0))}</td>}
                                    {loja === 'todas' && <td className={`${styles.right} ${styles.bold}`}>{formatCurrency(dadosMensal.meses.reduce((s, m) => s + m.ecommerce.valor, 0))}</td>}
                                    <td className={`${styles.right} ${styles.bold}`}>{formatCurrency(totalAnoAtual)}</td>
                                    {temComparacao && <td className={`${styles.right} ${styles.bold}`}>{formatCurrency(totalAnoComp)}</td>}
                                    {temComparacao && (
                                        <td className={styles.right}>
                                            {variacao !== null && (
                                                <span className={`${styles.varBadge} ${parseFloat(variacao) >= 0 ? styles.varUp : styles.varDown}`}>
                                                    {parseFloat(variacao) >= 0 ? '+' : ''}{variacao}%
                                                </span>
                                            )}
                                        </td>
                                    )}
                                    <td />
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </>
        );
    };

    // ============================
    // ABA: PRODUTOS EM QUEDA
    // ============================
    const renderProdutosQueda = () => {
        if (!dadosQueda || tipoPeriodo === 'mensal') return renderEmpty();

        const { analise, destaques, totais } = dadosQueda;

        const chartData = analise.map(m => ({
            nome: MESES_CURTO[m.mes - 1],
            [String(dadosQueda.ano)]: m.faturamentoAtual,
            [String(dadosQueda.anoAnterior)]: m.faturamentoAnterior,
        }));

        return (
            <>
                {/* Alerta */}
                {destaques.interanual ? (
                    <div className={`${styles.alertBox} ${styles.alertDanger}`}>
                        <span className={styles.alertEmoji}><FaExclamationTriangle color="#ef4444" /></span>
                        <div>
                            <div className={styles.alertTitle}>Queda detectada em relação a {dadosQueda.anoAnterior}</div>
                            <div className={styles.alertSub}>
                                Início: <strong>{destaques.interanual.nomeInicio}</strong> ·
                                Duração: <strong>{destaques.interanual.mesesConsecutivos} meses</strong> ·
                                Maior queda: <strong>{destaques.interanual.maiorQueda.toFixed(1)}%</strong>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className={`${styles.alertBox} ${styles.alertOk}`}>
                        <span className={styles.alertEmoji}><FaCheckCircle color="#22c55e" /></span>
                        <div>
                            <div className={styles.alertTitle}>Nenhuma queda significativa em relação a {dadosQueda.anoAnterior}</div>
                        </div>
                    </div>
                )}

                {/* KPIs */}
                <div className={styles.kpiRow}>
                    <div className={styles.kpiCard}>
                        <span className={styles.kpiLabel}>Total {dadosQueda.ano}</span>
                        <span className={styles.kpiValue}>{formatCurrency(totais.anoAtual)}</span>
                    </div>
                    <div className={styles.kpiCard}>
                        <span className={styles.kpiLabel}>Total {dadosQueda.anoAnterior}</span>
                        <span className={styles.kpiValue}>{formatCurrency(totais.anoAnterior)}</span>
                    </div>
                    <div className={styles.kpiCard}>
                        <span className={styles.kpiLabel}>Variação</span>
                        <span className={`${styles.kpiValue} ${totais.variacao >= 0 ? styles.textUp : styles.textDown}`}>
                            {totais.variacao >= 0 ? '▲' : '▼'} {Math.abs(totais.variacao)}%
                        </span>
                    </div>
                </div>

                {/* Gráfico */}
                <div className={styles.chartCard}>
                    <div className={styles.chartHeader}>{dadosQueda.ano} vs {dadosQueda.anoAnterior}</div>
                    <div className={styles.chartBody}>
                        <ResponsiveContainer width="100%" height={320}>
                            <ComposedChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                                <XAxis dataKey="nome" stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} />
                                <YAxis stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} tickFormatter={formatCompact} />
                                <Tooltip content={<ChartTooltip />} />
                                <Legend wrapperStyle={{ fontSize: '0.8rem', color: '#6b7280' }} />
                                <Area type="monotone" dataKey={String(dadosQueda.anoAnterior)} fill="rgba(203,213,225,0.2)" stroke="transparent" />
                                <Line type="monotone" dataKey={String(dadosQueda.anoAnterior)} stroke="#94a3b8" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="5 5" />
                                <Line type="monotone" dataKey={String(dadosQueda.ano)} stroke="#3b82f6" strokeWidth={2.5} dot={{ r: 4, fill: '#3b82f6' }} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Grid de meses */}
                <div className={styles.monthGrid}>
                    {analise.map(m => (
                        <div key={m.mes} className={`${styles.monthTile} ${m.status === 'queda' ? styles.monthDown : styles.monthUp}`}>
                            <div className={styles.monthTileName}>{MESES_CURTO[m.mes - 1]}</div>
                            <div className={styles.monthTileVal}>{formatCompact(m.faturamentoAtual)}</div>
                            {m.faturamentoAnterior > 0 ? (
                                <div className={`${styles.monthTileVar} ${m.variacao >= 0 ? styles.textUp : styles.textDown}`}>
                                    {m.variacao >= 0 ? '+' : ''}{m.variacao}%
                                </div>
                            ) : (
                                <div className={styles.monthTileVar} style={{ color: '#9ca3af' }}>—</div>
                            )}
                        </div>
                    ))}
                </div>
            </>
        );
    };

    // ============================
    // ABA: TOP VENDIDOS
    // ============================
    const renderTopVendidos = () => {
        if (!dadosTopVendidos) return renderEmpty();

        const top20 = dadosTopVendidos.slice(0, 20);
        const temComp = dadosComparacao !== null;
        const lblBase = tipoPeriodo === 'mensal' ? `${mesBase}/${anoBase}` : anoBase;
        const lblComp = tipoPeriodo === 'mensal' ? `${mesComp}/${anoComp || anoBase}` : anoComp;

        return (
            <div className={styles.chartCard} style={{ overflow: 'visible' }}>
                <div className={styles.chartHeader}><FaTrophy style={{ marginRight: '8px', color: '#fbbf24' }} /> Top 20 Produtos Mais Vendidos — {lblBase}</div>
                <div className={styles.tableWrap} style={{ overflow: 'visible' }}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ width: '40px', textAlign: 'center' }}>Pos</th>
                                <th style={{ width: '60px' }}>Img</th>
                                <th>Produto</th>
                                <th className={styles.right}>Qtd {lblBase}</th>
                                {temComp && <th className={styles.right}>Qtd {lblComp}</th>}
                                {temComp && <th className={styles.right}>Variação</th>}
                                <th className={styles.right}>Preço Médio</th>
                                <th className={styles.right}>Faturado ({lblBase})</th>
                            </tr>
                        </thead>
                        <tbody>
                            {top20.map((p, i) => {
                                let variacao = null;
                                if (temComp) {
                                    variacao = p.qtdBase > 0
                                        ? ((p.qtdComp - p.qtdBase) / p.qtdBase * 100).toFixed(1)
                                        : (p.qtdComp > 0 ? 100 : 0);
                                }

                                return (
                                    <tr key={p.codigo}>
                                        <td style={{ textAlign: 'center', fontWeight: 'bold', color: '#64748b', verticalAlign: 'middle' }}>{i + 1}º</td>
                                        <td style={{ verticalAlign: 'middle' }}>
                                            <ProductImageTooltip src={p.imagem} nome={p.nome} codigo={p.codigo} />
                                        </td>
                                        <td style={{ verticalAlign: 'middle' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                <div style={{ fontWeight: i < 3 ? 'bold' : 'normal', color: i === 0 ? '#fbbf24' : (i === 1 ? '#cbd5e1' : (i === 2 ? '#d97706' : '#e2e8f0')) }}>
                                                    {p.nome}
                                                </div>
                                                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{p.codigo}</div>
                                            </div>
                                        </td>
                                        <td className={`${styles.right} ${styles.bold}`} style={{ verticalAlign: 'middle', color: '#f8fafc' }}>{p.qtdBase} un</td>

                                        {temComp && (
                                            <td className={`${styles.right} ${styles.bold}`} style={{ verticalAlign: 'middle', color: '#94a3b8' }}>
                                                {p.qtdComp} un
                                            </td>
                                        )}

                                        {temComp && (
                                            <td className={styles.right} style={{ verticalAlign: 'middle' }}>
                                                <span className={`${styles.varBadge} ${parseFloat(variacao) >= 0 ? styles.varUp : styles.varDown}`}>
                                                    {parseFloat(variacao) >= 0 ? '+' : ''}{variacao}%
                                                </span>
                                            </td>
                                        )}

                                        <td className={styles.right} style={{ verticalAlign: 'middle' }}>{formatCurrency(p.precoVenda)}</td>
                                        <td className={`${styles.right} ${styles.bold}`} style={{ color: '#3b82f6', verticalAlign: 'middle' }}>{formatCurrency(p.valorBase)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    // ============================
    // ABA: TOP LUCRO
    // ============================
    const renderTopLucro = () => {
        if (!dadosMensal) return renderEmpty();

        const temComparacao = dadosComparacao !== null;

        // Ranking por contribuição percentual (Geral)
        const totalGeral = dadosMensal.meses.reduce((s, m) => s + m.total.valor, 0);
        const totalGeralComp = temComparacao ? dadosComparacao.meses.reduce((s, m) => s + m.total.valor, 0) : 0;

        const labelBase = tipoPeriodo === 'mensal' ? `${mesBase}/${anoBase}` : anoBase;
        const labelComp = tipoPeriodo === 'mensal' ? `${mesComp}/${anoComp || anoBase}` : anoComp;

        return (
            <div className={styles.chartCard}>
                <div className={styles.chartHeader}>
                    <FaCoins style={{ marginRight: '8px', color: '#fbbf24' }} /> Contribuição por {tipoPeriodo === 'mensal' ? 'Dia' : 'Mês'} — {temComparacao ? `${labelBase} vs ${labelComp}` : labelBase}
                </div>
                <div className={styles.tableWrap}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>{tipoPeriodo === 'mensal' ? 'Dia' : 'Mês'}</th>
                                {loja === 'todas' && <th className={styles.right}>Centro</th>}
                                {loja === 'todas' && <th className={styles.right}>Caxias</th>}
                                {loja === 'todas' && <th className={styles.right}>E-commerce</th>}
                                <th className={styles.right}>Total {labelBase}</th>
                                <th className={styles.right}>% {labelBase}</th>
                                {temComparacao && <th className={styles.right}>Total {labelComp}</th>}
                                {temComparacao && <th className={styles.right}>% {labelComp}</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {dadosMensal.meses.map((m, i) => {
                                const mComp = (temComparacao && dadosComparacao.meses[i]) ? dadosComparacao.meses[i] : null;
                                const valorBase = m.total.valor;
                                const valorComp = mComp ? mComp.total.valor : 0;

                                // Pular a linha se os dois anos estiverem zerados neste mês
                                if (valorBase === 0 && valorComp === 0) return null;

                                const pctAnoBase = totalGeral > 0 ? (valorBase / totalGeral * 100).toFixed(1) : 0;
                                const pctAnoComp = (temComparacao && totalGeralComp > 0) ? (valorComp / totalGeralComp * 100).toFixed(1) : 0;

                                return (
                                    <tr key={m.mes}>
                                        <td className={styles.monthName}>{m.nome}</td>
                                        {loja === 'todas' && <td className={styles.right}>{formatCurrency(m.centro.valor)}</td>}
                                        {loja === 'todas' && <td className={styles.right}>{formatCurrency(m.caxias.valor)}</td>}
                                        {loja === 'todas' && <td className={styles.right}>{formatCurrency(m.ecommerce.valor)}</td>}
                                        <td className={`${styles.right} ${styles.bold}`}>{formatCurrency(valorBase)}</td>
                                        <td className={styles.right}>
                                            <span className={styles.pctBadge}>{pctAnoBase}%</span>
                                        </td>
                                        {temComparacao && <td className={`${styles.right} ${styles.muted}`}>{formatCurrency(valorComp)}</td>}
                                        {temComparacao && (
                                            <td className={styles.right}>
                                                <span className={styles.pctBadge}>{pctAnoComp}%</span>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td className={styles.bold}>TOTAL</td>
                                {loja === 'todas' && <td className={styles.right}>—</td>}
                                {loja === 'todas' && <td className={styles.right}>—</td>}
                                {loja === 'todas' && <td className={styles.right}>—</td>}
                                <td className={`${styles.right} ${styles.bold}`}>{formatCurrency(totalGeral)}</td>
                                <td className={styles.right}><span className={styles.pctBadge}>100%</span></td>
                                {temComparacao && <td className={`${styles.right} ${styles.bold} ${styles.muted}`}>{formatCurrency(totalGeralComp)}</td>}
                                {temComparacao && <td className={styles.right}><span className={styles.pctBadge}>100%</span></td>}
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        );
    };

    // Estado vazio
    const renderEmpty = () => (
        <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                    <rect x="6" y="10" width="36" height="28" rx="3" stroke="#d1d5db" strokeWidth="2" fill="#f9fafb" />
                    <path d="M6 18h36" stroke="#d1d5db" strokeWidth="2" />
                    <rect x="12" y="14" width="3" height="1" rx="0.5" fill="#d1d5db" />
                    <rect x="17" y="14" width="3" height="1" rx="0.5" fill="#d1d5db" />
                    <rect x="22" y="14" width="3" height="1" rx="0.5" fill="#d1d5db" />
                    <path d="M18 28l4-4 4 4 6-6" stroke="#d1d5db" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </div>
            <div className={styles.emptyText}>Selecione os períodos e clique em Comparar</div>
        </div>
    );

    const ABAS = [
        { id: 'resumo', label: 'Resumo Geral', icon: <FaChartBar /> },
        ...(tipoPeriodo === 'anual' ? [{ id: 'queda', label: 'Produtos em Queda', icon: <FaChartLine /> }] : []),
        { id: 'topVendidos', label: 'Top Vendidos', icon: <FaTrophy /> },
        { id: 'topLucro', label: 'Top Lucro', icon: <FaCoins /> },
    ];

    return (
        <div className={styles.page}>
            {/* Header */}
            <div className={styles.pageHeader}>
                <div>
                    <h1 className={styles.pageTitle}><FaChartBar style={{ marginRight: '10px', color: '#3b82f6' }} /> Comparação de Vendas</h1>
                    <p className={styles.pageSubtitle}>Compare o desempenho entre períodos e identifique oportunidades</p>
                </div>
            </div>

            {/* Painel de Filtros */}
            <div className={styles.filterPanel}>
                <div className={styles.filterTop}>
                    <span className={styles.filterTitle}>Selecione o Período para Comparar</span>
                    <div className={styles.periodToggles}>
                        {['anual', 'mensal', 'semanal'].map(t => (
                            <button
                                key={t}
                                className={`${styles.periodBtn} ${tipoPeriodo === t ? styles.periodBtnActive : ''}`}
                                onClick={() => setTipoPeriodo(t)}
                            >
                                <FaCalendarAlt style={{ marginRight: '6px' }} /> {t.charAt(0).toUpperCase() + t.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>

                <div className={styles.filterRow}>
                    <div className={styles.filterField}>
                        <label className={styles.fieldLabel}>Ano Base</label>
                        <select
                            className={styles.fieldSelect}
                            value={anoBase}
                            onChange={(e) => setAnoBase(e.target.value)}
                        >
                            <option value="">Ano</option>
                            {anos.map(a => <option key={a} value={String(a)}>{a}</option>)}
                        </select>
                    </div>

                    <div className={styles.filterField}>
                        <label className={styles.fieldLabel}>Ano Comp.</label>
                        <select
                            className={styles.fieldSelect}
                            value={anoComp}
                            onChange={(e) => setAnoComp(e.target.value)}
                        >
                            <option value="">Ano</option>
                            {anos.filter(a => String(a) !== anoBase).map(a => (
                                <option key={a} value={String(a)}>{a}</option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.filterField}>
                        <label className={styles.fieldLabel}>Loja</label>
                        <select
                            className={styles.fieldSelect}
                            value={loja}
                            onChange={(e) => setLoja(e.target.value)}
                        >
                            <option value="todas">Todas as Lojas</option>
                            <option value="centro">Centro</option>
                            <option value="caxias">Caxias</option>
                            <option value="ecommerce">E-commerce</option>
                        </select>
                    </div>

                    {tipoPeriodo === 'mensal' && (
                        <>
                            <div className={styles.filterField}>
                                <label className={styles.fieldLabel}>Mês Base</label>
                                <select className={styles.fieldSelect} value={mesBase} onChange={(e) => setMesBase(e.target.value)}>
                                    <option value="1">Janeiro</option>
                                    <option value="2">Fevereiro</option>
                                    <option value="3">Março</option>
                                    <option value="4">Abril</option>
                                    <option value="5">Maio</option>
                                    <option value="6">Junho</option>
                                    <option value="7">Julho</option>
                                    <option value="8">Agosto</option>
                                    <option value="9">Setembro</option>
                                    <option value="10">Outubro</option>
                                    <option value="11">Novembro</option>
                                    <option value="12">Dezembro</option>
                                </select>
                            </div>
                            <div className={styles.filterField}>
                                <label className={styles.fieldLabel}>Mês Comp.</label>
                                <select className={styles.fieldSelect} value={mesComp} onChange={(e) => setMesComp(e.target.value)}>
                                    <option value="">Não Comparar Mês</option>
                                    <option value="1">Janeiro</option>
                                    <option value="2">Fevereiro</option>
                                    <option value="3">Março</option>
                                    <option value="4">Abril</option>
                                    <option value="5">Maio</option>
                                    <option value="6">Junho</option>
                                    <option value="7">Julho</option>
                                    <option value="8">Agosto</option>
                                    <option value="9">Setembro</option>
                                    <option value="10">Outubro</option>
                                    <option value="11">Novembro</option>
                                    <option value="12">Dezembro</option>
                                </select>
                            </div>
                        </>
                    )}

                    <button
                        className={styles.compareBtn}
                        onClick={handleComparar}
                        disabled={loading || loadingAnos || !anoBase}
                    >
                        {loading ? (
                            <span className={styles.btnSpinner} />
                        ) : (
                            <span className={styles.btnDot} />
                        )}
                        {loading ? 'Carregando...' : 'Comparar'}
                    </button>

                    {loadingAnos && (
                        <div className={styles.anosLoadingIndicator}>
                            <FaSpinner className={styles.anosSpinner} />
                            <span>Carregando anos...</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Abas de conteúdo */}
            <div className={styles.contentPanel}>
                <div className={styles.tabBar}>
                    {ABAS.map(aba => (
                        <button
                            key={aba.id}
                            className={`${styles.tabBtn} ${abaAtiva === aba.id ? styles.tabBtnActive : ''}`}
                            onClick={() => setAbaAtiva(aba.id)}
                        >
                            <span className={styles.tabIcon}>{aba.icon}</span>
                            {aba.label}
                        </button>
                    ))}
                </div>

                <div className={styles.tabContent}>
                    {loading ? (
                        <div className={styles.emptyState}>
                            <div className={styles.spinner} />
                            <div className={styles.emptyText}>Carregando dados...</div>
                        </div>
                    ) : !comparou ? (
                        renderEmpty()
                    ) : (
                        <>
                            {abaAtiva === 'resumo' && renderResumoGeral()}
                            {abaAtiva === 'queda' && renderProdutosQueda()}
                            {abaAtiva === 'topVendidos' && renderTopVendidos()}
                            {abaAtiva === 'topLucro' && renderTopLucro()}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default RelatoriosFaturamento;
